import appConfig from '../config/common';
import {v4 as uuidv4} from 'uuid';
import moment from 'moment';
import {api} from '../modules/Auth/constants/api';
import {refreshToken} from '../modules/Auth/actions';
import {showGlobalErrorMessage} from '../common/actions/globalErrorMessage/showGlobalErrorMessage';
import {pathnames as P} from 'common/constants/pathnames';
import {errors as E} from '../common/constants/errors';

const APP_LOGOUT = P.AUTH_LOGOUT;
const WHITELIST = [
  'steps/getStep11PDFData',
  'auth/preRegister',
  'auth/register',
  'auth/login',
];

export function setupAxios(axios, store) {
  axios.interceptors.request.use(
    async config => {
      if (config.url?.startsWith('https://ags.govmap.gov.il')) {
        return config;
      }

      if (config.url?.endsWith('https://maps.googleapis.com/maps/api/geocode/json')) {
        return config;
      }

      if (config.url?.endsWith('/files/getFile') && config.headers.accessToken) {
        return config;
      }

      config.baseURL = process.env.NODE_ENV === 'development'
        ? appConfig.api.dev_URL
        : appConfig.api.base_URL;
      config.headers['Content-Type'] = 'application/json';
      config.headers['requestid'] = `${uuidv4()}`;

      if (isURLInWhitelist(config.url) || config.headers.accessToken) {
        return config;
      }

      try {
        // requests that require access token in headers
        if (config.url?.endsWith('auth/refreshToken') || config.url?.endsWith('auth/logout')) {
          config.headers.accessToken = getAccessTokenFromStore(store);
        } else {
          config.headers.accessToken = `${await getAccessToken({store})}`;
        }

        if (!config.headers.accessToken) {
          console.log('access token was missed, try to log in again!');
          window.location.href = APP_LOGOUT;
        }

        return config;
      } catch (err) {
        console.error(err);
        throw new Error(err.message);
      }
    },
    error => {
      Promise.reject(error);
    }
  );
  axios.interceptors.response.use(
    response => {
      return response;
    },
    error => {
      if (!Boolean(error.response)
        && !isAuthError(error.response?.status)
        && !isLogout(error.response?.config)) {
        console.log(`it's not axios error`);
        window.location.href = APP_LOGOUT;
      }
      if (isAuthError(error.response?.status)
        && !isLogout(error.response?.config)) {
        console.log(`it's auth error`);
        window.location.href = APP_LOGOUT;
      }
      if (isInternalServerError(error.response?.status)
        && !isLogout(error.response?.config
          && !error.response?.config?.url?.endsWith('auth/refreshToken'))) {
        console.log(`it's common error, not a refresh token`);
        showGlobalErrorMessage(E.INTERNAL_SERVER_ERROR, () => window.location.href = APP_LOGOUT)(store.dispatch);
      }

      return Promise.reject(error);
    });
}

const getAccessToken = async ({store}) => {
  const urlToken = new URLSearchParams(window.location.search).get('accessToken');

  if (urlToken) {
    return urlToken;
  }

  if (checkTokenExpire(getExpireTimeFromStore(store))) {
    return await refreshToken(getAccessTokenFromStore(store))(store.dispatch);
  } else {
    return getAccessTokenFromStore(store);
  }
};

const getAccessTokenFromStore = store => store.getState().auth.accessToken;
const getExpireTimeFromStore = store => store.getState().auth.expire;

const checkTokenExpire = expire => {
  const expireMoment = moment.unix(expire).subtract(appConfig.api.refresh_gap);
  const currentMoment = moment();

  return currentMoment.isSameOrAfter(expireMoment);
};

const isAuthError = status => status >= 400 && status < 500;
const isLogout = config => config.url?.endsWith(api.LOGOUT);
const isInternalServerError = status => status >= 500;

const isURLInWhitelist = url => {
  return WHITELIST.reduce((result, whiteURL) => {
    return whiteURL.endsWith(url) || result;
  }, false);
};

